import * as React from 'react'
import clsx from 'clsx'
import { SxProps, sx as sxFunc } from '../sx'
import { useFormControl, formControlState } from '../FormControl'
import style from './index.module.scss'

export interface FormHelperTextClasses {
  /** Styles applied to the root element. */
  root: string
  /** State class applied to the root element if `error={true}`. */
  error: string
  /** State class applied to the root element if `disabled={true}`. */
  disabled: string
  /** Styles applied to the root element if `size="small"`. */
  sizeSmall: string
  /** Styles applied to the root element if `size="medium"`. */
  sizeMedium: string
  /** Styles applied to the root element if `size="large"`. */
  sizeLarge: string
  /** Styles applied to the root element if `variant="filled"` or `variant="outlined"`. */
  contained: string
  /** State class applied to the root element if `focused={true}`. */
  focused: string
  /** State class applied to the root element if `filled={true}`. */
  filled: string
  /** State class applied to the root element if `required={true}`. */
  required: string
}

export interface FormHelperTextProps
  extends React.HTMLAttributes<HTMLParagraphElement> {
  /**
   * The content of the component.
   *
   * If `' '` is provided, the component reserves one line height for displaying a future message.
   */
  children?: React.ReactNode
  /**
   * Override or extend the styles applied to the component.
   */
  classes?: Partial<FormHelperTextClasses>
  /**
   * The component used for the root node.
   * Either a string to use a HTML element or a component.
   * @default p
   */
  Component?: React.ElementType
  /**
   * If `true`, the helper text should be displayed in a disabled state.
   */
  disabled?: boolean
  /**
   * If `true`, helper text should be displayed in an error state.
   */
  error?: boolean
  /**
   * If `true`, the helper text should use filled classes key.
   */
  filled?: boolean
  /**
   * If `true`, the helper text should use focused classes key.
   */
  focused?: boolean
  /**
   * If `dense`, will adjust vertical spacing. This is normally obtained via context from
   * FormControl.
   */
  margin?: 'dense'
  /**
   * If `true`, the helper text should use required classes key.
   */
  required?: boolean
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx?: SxProps
  /**
   * The variant to use.
   */
  variant?: 'standard' | 'outlined' | 'filled'
}

export const FormHelperText = React.forwardRef<
  HTMLParagraphElement,
  FormHelperTextProps
>(function FormHelperText(props, ref) {
  const {
    children,
    className,
    classes,
    Component = 'p',
    disabled,
    error,
    filled,
    focused,
    required,
    variant,
    sx,
    ...other
  } = props

  const factorFormControl = useFormControl()
  const fcs = formControlState({
    props: { variant, disabled, error, filled, focused, required },
    factorFormControl,
    states: [
      'variant',
      'size',
      'disabled',
      'error',
      'filled',
      'focused',
      'required',
    ],
  })

  const _classes = clsx(
    classes?.root,
    style.root,
    (fcs.variant === 'filled' || fcs.variant === 'outlined') && [
      style.contained,
      classes?.contained,
    ],
    fcs.size === 'small' && [style['size-small'], classes?.sizeSmall],
    fcs.size === 'medium' && [style['size-medium'], classes?.sizeMedium],
    fcs.size === 'large' && [style['size-large'], classes?.sizeLarge],
    fcs.disabled && [style.disabled, classes?.disabled],
    fcs.error && [style.error, classes?.error],
    fcs.focused && [classes?.focused],
    fcs.required && [classes?.required],
    fcs.filled && [classes?.filled],
    className,
    sxFunc(sx)
  )

  return (
    <Component className={_classes} ref={ref} {...other}>
      {children === ' ' ? (
        // notranslate needed while Google Translate will not fix zero-width space issue
        <span className="notranslate">&#8203;</span>
      ) : (
        children
      )}
    </Component>
  )
})
